import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Award,
  Briefcase,
  Calendar,
  RefreshCw,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import DepartmentDonut from "@/components/admin/charts/DepartmentDonut";
import AppErrorState from "@/components/ui/AppErrorState";
import AppLoadingState from "@/components/ui/AppLoadingState";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { getRecruitmentStats } from "@/lib/recruitmentApi";
import { formatNumber } from "@/lib/pilotageUi";
import { TESTIDS } from "@/lib/testids";

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  testId,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass rounded-xl p-6"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm text-slate-400">{title}</p>
        <p className="mt-1 text-3xl font-bold text-white">{value}</p>
        {subtitle ? (
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        ) : null}
      </div>
      <div className={`rounded-lg p-3 ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
    </div>
  </motion.div>
);

const SectionHeading = ({ eyebrow, title, description }) => (
  <div className="space-y-1">
    <p className="text-xs font-semibold uppercase tracking-widest text-lenvia-blue/80">
      {eyebrow}
    </p>
    <h2 className="text-xl font-heading font-bold text-white">{title}</h2>
    <p className="text-sm text-slate-400">{description}</p>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const currentValue = payload[0];
  const tooltipLabel =
    currentValue.payload?.fullName ||
    currentValue.payload?.fullLabel ||
    label;

  return (
    <div className="rounded-lg border border-white/10 bg-slate-900 p-3 shadow-xl">
      <p className="font-medium text-white">{tooltipLabel}</p>
      <p className="text-lenvia-blue">
        {formatNumber(currentValue.value)} candidature
        {currentValue.value > 1 ? "s" : ""}
      </p>
    </div>
  );
};

const buildMonthLabel = (monthValue, options) =>
  new Date(`${monthValue}-01`).toLocaleDateString("fr-FR", options);

const RecruitmentStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const nextStats = await getRecruitmentStats();
      setStats(nextStats);
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Erreur lors du chargement des statistiques RH",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchStats();
  }, []);

  if (loading) {
    return (
      <AppLoadingState
        title="Chargement des statistiques RH"
        description="Préparation des tendances, répartitions et top offres."
        testId="recruitment-stats-loading"
      />
    );
  }

  if (error) {
    return (
      <AppErrorState
        title="Statistiques RH indisponibles"
        message={error}
        onAction={fetchStats}
        testId="recruitment-stats-error"
      />
    );
  }

  const statsValue = stats || {};
  const monthlyData = (statsValue.evolution_mensuelle || []).map((item) => ({
    count: Number(item.candidatures ?? 0),
    fullLabel: buildMonthLabel(item.mois, {
      month: "long",
      year: "numeric",
    }),
    label: buildMonthLabel(item.mois, {
      month: "short",
      year: "2-digit",
    }),
  }));
  const departmentData = (statsValue.repartition_departements || []).map((item) => ({
    count: Number(item.candidatures ?? 0),
    department: item.departement || "Non defini",
  }));
  const topOffers = statsValue.top_offres || [];
  const topOffersData = topOffers.map((offer) => ({
    candidatures: Number(offer.candidatures ?? 0),
    fullName: offer.titre || "Sans titre",
    name:
      (offer.titre || "").length > 22
        ? `${offer.titre.slice(0, 22)}...`
        : offer.titre || "Sans titre",
  }));

  return (
    <div className="space-y-8" data-testid={TESTIDS.recruitment.dashboard.page}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <Link
            to="/admin/recrutement/dashboard"
            className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour au dashboard RH
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">
              Statistiques RH
            </h1>
            <p className="mt-1 text-slate-400">
              Vue complete du recrutement: KPIs, tendances et performance des
              offres.
            </p>
          </div>
        </div>
        <Button
          onClick={fetchStats}
          variant="outline"
          size="sm"
          className="border-white/10 text-slate-300"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Actualiser
        </Button>
      </div>

      <section className="space-y-4">
        <SectionHeading
          eyebrow="KPI"
          title="Indicateurs de synthese"
          description="Les quatre mesures essentielles du recrutement restent visibles en tete."
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Offres actives"
            value={formatNumber(Number(statsValue.offres_actives ?? 0))}
            subtitle="Postes ouverts"
            icon={Briefcase}
            color="bg-lenvia-blue"
            testId={TESTIDS.recruitment.dashboard.kpiActiveOffers}
          />
          <StatCard
            title="Total candidatures"
            value={formatNumber(
              Number(statsValue.total_candidatures ?? 0),
            )}
            subtitle="Toutes offres confondues"
            icon={Users}
            color="bg-cyan-500"
            testId={TESTIDS.recruitment.dashboard.kpiTotalApplications}
          />
          <StatCard
            title="Moyenne / offre"
            value={formatNumber(Number(statsValue.moyenne_par_offre ?? 0), {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
            subtitle="Candidatures par poste actif"
            icon={TrendingUp}
            color="bg-emerald-500"
            testId="recruitment-kpi-moyenne-par-offre"
          />
          <StatCard
            title="Taux de conversion"
            value={`${formatNumber(Number(statsValue.taux_conversion ?? 0), {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}%`}
            subtitle="Candidatures sorties du statut Nouveau"
            icon={Award}
            color="bg-amber-500"
            testId="recruitment-kpi-taux-conversion"
          />
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading
          eyebrow="Analytics"
          title="Analyse detaillee"
          description="Le suivi complet est isole ici pour ne pas alourdir le dashboard RH."
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-6"
          data-testid="recruitment-chart-monthly"
        >
          <div className="mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-lenvia-blue" />
            <h3 className="text-lg font-semibold text-white">
              Evolution mensuelle des candidatures
            </h3>
          </div>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#334155" }}
                />
                <YAxis
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#334155" }}
                />
                <Tooltip content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3B82F6"
                  strokeWidth={3}
                  dot={{ fill: "#3B82F6", r: 4, strokeWidth: 0 }}
                  activeDot={{ fill: "#3B82F6", r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[320px] items-center justify-center text-slate-500">
              Pas de donnees disponibles
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-xl p-6"
            data-testid="recruitment-chart-departments"
          >
            <DepartmentDonut
              data={departmentData}
              title="Repartition par departement"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-xl p-6"
            data-testid="recruitment-chart-top-offers"
          >
            <h3 className="mb-4 text-lg font-semibold text-white">
              Top offres par candidatures
            </h3>
            {topOffersData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topOffersData} layout="vertical">
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#1e293b"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    axisLine={{ stroke: "#334155" }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    axisLine={{ stroke: "#334155" }}
                    width={110}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar
                    dataKey="candidatures"
                    fill="#3B82F6"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[280px] items-center justify-center text-slate-500">
                Pas de donnees disponibles
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RecruitmentStatsPage;
